import { computed, readonly, ref, useContext, useRoute } from '@nuxtjs/composition-api';

import { ComposableFunctionArgs, useUiNotification } from '~/composables';
import { useI18n } from '~/helpers/hooks/usei18n';
import { Logger } from '~/helpers/logger';
import { getPrice as getProductPrice } from '~/modules/catalog/product/getters/productGetters';
import { Product } from '~/modules/catalog/product/types';
import { addItemCommand } from '~/modules/checkout/composables/useCart/commands/addItemCommand';
import { applyCouponCommand } from '~/modules/checkout/composables/useCart/commands/applyCouponCommand';
import { loadCartCommand } from '~/modules/checkout/composables/useCart/commands/loadCartCommand';
import { loadTotalQtyCommand } from '~/modules/checkout/composables/useCart/commands/loadTotalQtyCommand';
import { removeCouponCommand } from '~/modules/checkout/composables/useCart/commands/removeCouponCommand';
import { removeItemCommand } from '~/modules/checkout/composables/useCart/commands/removeItemCommand';
import { updateItemQtyCommand } from '~/modules/checkout/composables/useCart/commands/updateItemQtyCommand';
import { useCartStore } from '~/modules/checkout/stores/cart';
import { Cart, CartItemInterface, ProductInterface, ProductStockStatus } from '~/modules/GraphQL/types';
import { useWishlist } from '~/modules/wishlist/composables/useWishlist';

import {
  acknowledgeCartRead,
  beginCartRead,
  canApplyCartRead,
  CartContextChangedError,
  CartContextIdentity,
  cartReadContractKey,
  CartResultUnconfirmedError,
  enqueueCartMutation,
  getCartReadInFlight,
  hasConfirmedCartQuantity,
  hasCurrentCartData,
  hasCurrentCartSnapshot,
  markCartChanged,
  needsCartReconciliation,
  rememberCartDataOwner,
  rememberCartReadSnapshot,
  rememberConfirmedCartQuantity,
  shareCartRead,
} from './cartCoordinator';
import { CartFullReadState, UseCartErrors, UseCartInterface } from './useCart';

/**
 * Allows loading and manipulating cart of the current user.
 *
 * See the {@link UseCartInterface} for a list of methods and values available in this composable.
 */
export function useCart<CART extends Cart, CART_ITEM extends CartItemInterface, PRODUCT extends ProductInterface>(): UseCartInterface<
CART,
CART_ITEM,
PRODUCT
> {
  const loading = ref<boolean>(false);
  const lastFullReadState = ref<CartFullReadState>('idle');
  let fullReadSequence = 0;
  const error = ref<UseCartErrors>({
    addItem: null,
    removeItem: null,
    updateItemQty: null,
    load: null,
    clear: null,
    applyCoupon: null,
    removeCoupon: null,
    loadTotalQty: null,
  });
  const { app } = useContext();
  const context = app.$vsf;
  const route = useRoute();
  const { locale } = useI18n();
  const cartStore = useCartStore();
  const cart = computed(() => cartStore.cart as CART);
  const apiState = context.$magento.config.state;
  const { loading: wishlistLoading, afterAddingWishlistItemToCart } = useWishlist();
  const { send: sendNotification } = useUiNotification();
  const getIdentity = (): CartContextIdentity => ({
    cartId: apiState.getCartId?.() || '',
    customer: apiState.getCustomerToken?.() || '',
    store: apiState.getStore?.() || '',
    locale: apiState.getLocale?.() || '',
    currency: apiState.getCurrency?.() || '',
  });
  const assertLoadedCart = () => {
    if (!cart.value?.id || cart.value.id !== apiState.getCartId()) {
      throw new CartContextChangedError();
    }
  };
  const confirmedCart = (updatedCart: CART): CART => {
    if (!updatedCart?.id || updatedCart.id !== apiState.getCartId() || !Array.isArray(updatedCart.items)) {
      throw new CartResultUnconfirmedError();
    }
    return updatedCart;
  };
  const notifyUnconfirmed = (failure: Error) => {
    if (failure instanceof CartResultUnconfirmedError) {
      sendNotification({
        id: Symbol('cart_refresh_required'),
        message: failure.message,
        type: 'danger',
        icon: 'info',
        persist: false,
      });
    }
  };

  /**
   * Assign new cart object
   * @param newCart
   *
   * @return void
   */
  const setCart = (newCart: CART, readContract: ComposableFunctionArgs<{ realCart?: boolean }> = {}): void => {
    Logger.debug('useCart.setCart', newCart);

    markCartChanged(cartStore);
    needsCartReconciliation(cartStore, getIdentity());
    cartStore.$patch((state) => {
      state.cart = newCart;
    });
    rememberCartDataOwner(cartStore, getIdentity(), cart.value, cartReadContractKey(readContract));
  };

  const setConfirmedMutationCart = (newCart: CART, customHeaders = {}): void => {
    setCart(confirmedCart(newCart), { customHeaders });
    // A custom header contract must not seed the ordinary header badge.
    if (Object.keys(customHeaders ?? {}).length === 0) {
      rememberConfirmedCartQuantity(cartStore, getIdentity(), cart.value);
    }
  };

  /**
   * Check if product is in the cart
   * @param product
   *
   * @return boolean
   */
  const isInCart = (product: PRODUCT): boolean => !!cart.value?.items?.find((cartItem) => cartItem?.product?.uid === product.uid);

  const load = async (
    { customQuery = {}, customHeaders = {}, realCart = false, reuseExisting = false, force = false }: ComposableFunctionArgs<{
      realCart?: boolean; reuseExisting?: boolean; force?: boolean;
    }> = { customQuery: { cart: 'cart' }, customHeaders: {} }
  ): Promise<void> => {
    Logger.debug('***** useCart.load *******');
    const sequence = ++fullReadSequence;
    lastFullReadState.value = 'loading';

    try {
      loading.value = true;
      needsCartReconciliation(cartStore, getIdentity());
      const contract = cartReadContractKey({ customQuery, customHeaders, realCart });
      let state: CartFullReadState = 'applied';
      if (!reuseExisting || force || !hasCurrentCartSnapshot(cartStore, getIdentity(), contract, cart.value)) {
        state = await shareCartRead<CartFullReadState>(cartStore, getIdentity(), contract, force, async (read) => {
          const loadedCart = await loadCartCommand.execute(context, { customQuery, customHeaders, realCart });
          if (!canApplyCartRead(cartStore, read, getIdentity(), loadedCart?.id)) return 'discarded';
          acknowledgeCartRead(cartStore, read, getIdentity(), loadedCart?.id);
          setCart(loadedCart as CART, { customQuery, customHeaders, realCart });
          rememberCartDataOwner(cartStore, getIdentity(), cart.value, contract);
          rememberCartReadSnapshot(cartStore, getIdentity(), contract, cart.value, read.sequence);
          rememberConfirmedCartQuantity(cartStore, getIdentity(), cart.value, contract, read.sequence);
          return 'applied';
        });
      }
      if (sequence === fullReadSequence) {
        lastFullReadState.value = state;
        error.value.load = null;
      }
    } catch (err) {
      if (sequence === fullReadSequence) {
        lastFullReadState.value = err instanceof CartContextChangedError ? 'discarded' : 'failed';
        error.value.load = err;
      }
      console.error('!!! useCart/load Error:', JSON.stringify(err));
      // sendNotification({
      //   id: Symbol('loadCart_error'),
      //   message: err.message ? `${err.message}. ${app.i18n.t('RefreshPage')}` : app.i18n.t('Login expired, please log in again') as string,
      //   type: 'danger',
      //   icon: 'cross',
      //   persist: false,
      // });
    } finally {
      if (sequence === fullReadSequence) loading.value = false;
    }
  };

  const clear = async ({ customQuery, customHeaders } = { customQuery: { cart: 'cart' }, customHeaders: {} }): Promise<void> => {
    Logger.debug('-- useCart.clear start -- ');

    try {
      loading.value = true;
      await enqueueCartMutation(cartStore, getIdentity, async (guard) => {
        context.$magento.config.state.removeCartId();
        guard.adoptCartId('');
        const loadedCart = await loadCartCommand.execute(context, { customQuery, customHeaders });
        guard.adoptCartId(loadedCart?.id || '');
        setCart(loadedCart as CART);
      });
      error.value.clear = null;
    } catch (err) {
      error.value.clear = err;
      Logger.error('useCart/clear', err);
    } finally {
      loading.value = false;
    }
  };

  const loadTotalQty = async (params?: ComposableFunctionArgs<{}>): Promise<void> => {
    Logger.debug('useCart.loadTotalQty');

    try {
      loading.value = true;
      const customQuantityContract = Object.keys(params?.customQuery ?? {}).length > 0
        || Object.keys(params?.customHeaders ?? {}).length > 0;
      if (!customQuantityContract) {
        if (hasConfirmedCartQuantity(cartStore, getIdentity(), cart.value)) return;
        const pendingFullRead = getCartReadInFlight(cartStore, getIdentity(), cartReadContractKey());
        if (pendingFullRead !== undefined) {
          // The full read already contains total_quantity. Do not race it with
          // a second quantity-only request or retry it if that read fails.
          await pendingFullRead;
          return;
        }
      }
      if (cartStore.pendingMutations > 0 || needsCartReconciliation(cartStore, getIdentity())) return;
      const read = beginCartRead(cartStore, getIdentity(), 'quantity');
      const totalQuantity = await loadTotalQtyCommand.execute(context, params);
      if (canApplyCartRead(cartStore, read, getIdentity())
        && (!cart.value?.id || cart.value.id === apiState.getCartId())) {
        cartStore.$patch((state) => {
          if (state.cart) state.cart.total_quantity = totalQuantity;
        });
      }
    } catch (err) {
      error.value.loadTotalQty = err;
      Logger.error('==> useCart/loadTotalQty err', err);
      let errMsg = '';
      if (err.message?.toLowerCase().includes('active')) {
        errMsg = 'Cart Not Active';
        // sendNotification({
        //   id: Symbol('loadTotalQty_error'),
        //   message: app.i18n.t(errMsg) as string,
        //   type: 'warning',
        //   icon: null,
        //   persist: false,
        // });
      } else {
        errMsg = 'Login expired, please log in again';
      }
      console.log('==> [load cart qty error msg:]', errMsg);
    } finally {
      loading.value = false;
    }
  };

  const addItem = async ({
    product,
    quantity,
    productConfiguration,
    customQuery = { addSimpleProductsToCart: locale === 'cn' ? 'cart-add-item-cn' : 'cart-add-item' },
    customHeaders,
  }): Promise<void> => {
    Logger.debug('useCart.addItem', { product, quantity });

    try {
      loading.value = true;
      await enqueueCartMutation(cartStore, getIdentity, async (guard) => {
        const knownCartId = apiState.getCartId();
        if (!knownCartId) {
          const createdCart = await loadCartCommand.execute(context, { realCart: true });
          guard.adoptCartId(createdCart?.id || '');
          setCart(createdCart as CART);
        } else if (!cart.value?.id) {
          // A restored cart cookie may precede Pinia hydration. Resolve that
          // same cart before the first write, without adopting another owner
          // or treating an already-loaded mismatched cart as the current one.
          guard.assertCurrent();
          const loadedCart = await loadCartCommand.execute(context, { realCart: true });
          guard.assertCurrent();
          if (loadedCart?.id !== knownCartId || !Array.isArray(loadedCart.items)) {
            throw new CartContextChangedError();
          }
          setCart(loadedCart as CART);
        }
        assertLoadedCart();
        const updatedCart = await addItemCommand.execute(context, {
          currentCart: cart.value,
          product,
          quantity,
          productConfiguration,
          customQuery,
          customHeaders,
        });
        guard.assertCurrent();
        setConfirmedMutationCart(updatedCart as CART, customHeaders);
        error.value.addItem = null;
        // Analytics must not turn an accepted write into a reported failure.
        try {
          if (typeof window !== 'undefined' && window.dataLayer) {
            const productPriceObj = getProductPrice(product);
            const gtagItems = {
              item_id: product.sku, // required
              item_name: product.name, // required
              item_brand: product.brands ?? '',
              price: productPriceObj.special ?? productPriceObj.regular,
              quantity,
            };
            window.dataLayer.push({
              event: 'add_to_cart',
              ecommerce: {
                currency: apiState.getCurrency(),
                value: productPriceObj.special ?? productPriceObj.regular,
                items: [gtagItems], // required
              },
            });
          }
        } catch (analyticsError) {
          Logger.error('useCart/addItem analytics', analyticsError);
        }
        sendNotification({
          id: Symbol('add_to_cart_success'),
          message: app.i18n.t('Add To Cart Successfully') as string,
          type: 'success',
          icon: 'check',
          persist: false,
        });
      });
    } catch (err) {
      error.value.addItem = err;
      notifyUnconfirmed(err);
      Logger.error('** useCart/addItem Error:', err);
      console.log(err.toString());
      if (err && (err.toString().includes('isn\'t active') || err.toString().includes('Could not find a cart'))) {
        // sendNotification({
        //   id: Symbol('add_to_cart_error'),
        //   message: app.i18n.t('Add To Cart Failed, please try again') as string,
        //   type: 'danger',
        //   icon: 'check',
        //   persist: false,
        // });
        // clear cookie cart
        console.log('* * need to cleat current cart **');
        await clear();
        sendNotification({
          id: Symbol('add_to_cart_error'),
          message: app.i18n.t('please try it again') as string,
          type: 'info',
          icon: 'check',
          persist: false,
        });
      }
    } finally {
      if (!wishlistLoading.value && route.value.query?.wishlist) {
        afterAddingWishlistItemToCart({
          product,
          cartError: error.value.addItem,
        });
      }
      loading.value = false;
    }
  };

  const removeItem = async ({
    product,
    customQuery = { removeItemFromCart: locale === 'cn' ? 'cart-cn-remove-item' : 'cart-remove-item' },
    customHeaders,
  }) => {
    Logger.debug('useCart.removeItem', { product });

    try {
      loading.value = true;
      await enqueueCartMutation(cartStore, getIdentity, async (guard) => {
        assertLoadedCart();
        const updatedCart = await removeItemCommand.execute(context, {
          currentCart: cart.value,
          product,
          customQuery,
          customHeaders,
        });
        guard.assertCurrent();
        setConfirmedMutationCart(updatedCart as CART, customHeaders);
        error.value.removeItem = null;
      });
    } catch (err) {
      error.value.removeItem = err;
      console.error('[useCart/removeItem error]:', JSON.stringify(err));
      sendNotification({
        id: Symbol('remove_item_cart_error'),
        message: err.message ?? (app.i18n.t('Remove Failed, please try again') as string),
        type: 'danger',
        icon: 'check',
        persist: false,
      });
      throw err;
    } finally {
      // await load();
      loading.value = false;
    }
  };

  const updateItemQty = async ({
    product,
    quantity,
    customQuery = { updateCartItems: locale === 'cn' ? 'cart-cn-update-item-qty' : 'cart-update-item-qty' },
  }): Promise<void> => {
    Logger.debug('useCart.updateItemQty', {
      product,
      quantity,
    });

    if (quantity && quantity > 0) {
      try {
        loading.value = true;
        await enqueueCartMutation(cartStore, getIdentity, async (guard) => {
          assertLoadedCart();
          const updatedCart = await updateItemQtyCommand.execute(context, {
            currentCart: cart.value,
            product,
            quantity,
            customQuery,
          });
          guard.assertCurrent();
          setConfirmedMutationCart(updatedCart as CART);
          error.value.updateItemQty = null;
        });
      } catch (err) {
        error.value.updateItemQty = err;
        Logger.error('useCart/updateItemQty', err);
        sendNotification({
          id: Symbol('update cart qty error'),
          message: err.message || app.i18n.t('Unable to update your cart.'),
          type: 'danger',
          icon: '',
          persist: false,
          title: 'Error!',
        });
      } finally {
        loading.value = false;
      }
    }
  };

  const handleCoupon = async (couponCode = null, customQuery = null, _isChangingCode = false): Promise<CART> => {
    assertLoadedCart();
    const variables = {
      currentCart: cart.value,
      customQuery,
      couponCode,
    };

    const { updatedCart, errors } = couponCode
      ? await applyCouponCommand.execute(context, {
        ...variables,
        customQuery: { applyCouponToCart: locale === 'cn' ? 'cart-apply-coupon-cn' : 'cart-apply-coupon' },
      })
      : await removeCouponCommand.execute(context, {
        ...variables,
        customQuery: { removeCouponFromCart: locale === 'cn' ? 'cart-remove-coupon-cn' : 'cart-remove-coupon' },
      });

    if (errors?.length) {
      sendNotification({
        id: Symbol('handleCoupon_error'),
        message: app.i18n.t(errors[0].message) as string,
        persist: false,
        type: 'danger',
        icon: 'check',
        title: 'Error Happened',
      });
      throw errors[0];
    }

    return confirmedCart(updatedCart as CART);

    // if (updatedCart) {
    //   if (isChangingCode) {
    //     cartStore.$patch((state) => {
    //       state.cart = updatedCart;
    //     });
    //   } else {
    //     await load();
    //   }
    // }
  };

  const applyCoupon = async ({ couponCode, customQuery }): Promise<void> => {
    Logger.debug('useCart.applyCoupon');

    try {
      loading.value = true;
      await enqueueCartMutation(cartStore, getIdentity, async (guard) => {
        const updatedCart = await handleCoupon(couponCode, customQuery);
        guard.assertCurrent();
        setConfirmedMutationCart(updatedCart);
        error.value.applyCoupon = null;
      });
    } catch (err) {
      error.value.applyCoupon = err;
      notifyUnconfirmed(err);
      Logger.error('useCart/applyCoupon', err);
    } finally {
      loading.value = false;
    }
  };

  const removeCoupon = async ({ customQuery, couponCode = null, isChangingCode = false }): Promise<void> => {
    Logger.debug('useCart.removeCoupon');

    try {
      loading.value = true;
      await enqueueCartMutation(cartStore, getIdentity, async (guard) => {
        const updatedCart = await handleCoupon(couponCode, customQuery, isChangingCode);
        guard.assertCurrent();
        setConfirmedMutationCart(updatedCart);
        error.value.removeCoupon = null;
      });
    } catch (err) {
      error.value.removeCoupon = err;
      notifyUnconfirmed(err);
      Logger.error('useCart/removeCoupon', err);
    } finally {
      loading.value = false;
    }
  };

  const canAddToCart = (product: Product, qty = 1) => {
    // eslint-disable-next-line no-underscore-dangle
    if (product?.__typename === 'ConfigurableProduct') {
      return !!product?.configurable_product_options_selection?.variant?.uid;
    }
    const inStock = product?.stock_status === ProductStockStatus.InStock;
    const stockLeft = product?.only_x_left_in_stock ? qty <= product?.only_x_left_in_stock : true;
    return inStock && stockLeft;
  };

  return {
    setCart,
    cart,
    loadTotalQty,
    isInCart,
    addItem,
    load,
    removeItem,
    clear,
    updateItemQty,
    applyCoupon,
    removeCoupon,
    canAddToCart,
    loading: computed(() => loading.value || cartStore.pendingMutations > 0),
    needsReconciliation: computed(() => needsCartReconciliation(cartStore, getIdentity())),
    lastFullReadState: readonly(lastFullReadState),
    // Cookie identity getters are not Vue-reactive. Check provenance at call
    // time instead of caching it in a computed that could survive login.
    isCurrentData: (readContract = {}) => hasCurrentCartData(cartStore, getIdentity(), cart.value, cartReadContractKey(readContract)),
    error: readonly(error),
  };
}

export default useCart;
export * from './useCart';
